import { Typography } from "@king-ict/eupisi-ui/components/Typography/Typography";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import StaticField from "../../components/StaticField";
import { ProgramType } from "../../models/Program/ProgramType";
import { LearningSet } from "../../models/ProgramRequest/LearningSet";
import { Module } from "../../models/ProgramRequest/Module";
import { ModuleInfo } from "../ProgramRequestEdit/ModuleInfo";
import styles from "./ProgramRequestDetails.module.scss";

type ProgramRequestModuleProps = {
  name: string;
  modules: Module[];
  programTypeId: number;
  correctionModules?: Module[];
  qualificationTypeId?: number;
  readonly?: boolean;
  getErrorForPath: (path: string) => string | undefined;
  onChange: (e: any) => void;
}

/** In readonly mode, displays a text header row and several rows 
 * for each module including learning sets. If there are no modules, 
 * nothing is rendered. 
 * In editable mode, displays 
 * a ModuleInfo component for adding/removing modules and 
 * learning sets.
 */
export default function ProgramRequestModulePanel({
  name,
  modules,
  programTypeId,
  correctionModules,
  qualificationTypeId = 0,
  readonly = false,
  getErrorForPath,
  onChange,
}: ProgramRequestModuleProps): JSX.Element {
  const { t } = useTranslation();

  const handleModuleAdd = (event: React.MouseEvent<any, MouseEvent>) => {
    var newList = modules.slice();
    newList.push({ name: "", learningSets: [{ name: "", code: "", csvet: 1, level: 2 }] });
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newList
      }
    });
  }

  const handleModuleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    var newList = modules.slice();
    newList[index].name = event.target.value;
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newList
      }
    });
  }

  const handleModuleRemove = (event: React.MouseEvent<any, MouseEvent>, index: number) => {
    var newList = modules.slice();
    newList.splice(index, 1);
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newList
      }
    });
  }

  const handleLearningSetAdd = (event: React.MouseEvent<any, MouseEvent>, index: number) => {
    var newList = modules.slice();
    modules[index].learningSets.push({ name: "", code: "", csvet: 1, level: 2 })
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newList
      }
    });
  }

  const handleLearningSetNameChange = (event: React.ChangeEvent<HTMLInputElement>, modIndex: number, lsIndex: number) => {
    var newList = modules.slice();
    modules[modIndex].learningSets[lsIndex].name = event.target.value;
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newList
      }
    });
  }

  const handleLearningSetCodeChange = (event: React.ChangeEvent<HTMLInputElement>, modIndex: number, lsIndex: number) => {
    var newList = modules.slice();
    modules[modIndex].learningSets[lsIndex].code = event.target.value;
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newList
      }
    });
  }

  const handleLearningSetLevelChange = (event: React.ChangeEvent<HTMLInputElement>, modIndex: number, lsIndex: number) => {
    var newList = modules.slice();
    modules[modIndex].learningSets[lsIndex].level = parseInt(event.target.value);
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newList
      }
    });
  }

  const handleLearningSetcSVETChange = (event: React.ChangeEvent<HTMLInputElement>, modIndex: number, lsIndex: number) => {
    var newList = modules.slice();
    modules[modIndex].learningSets[lsIndex].csvet = parseInt(event.target.value);
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newList
      }
    });
  }

  const handleLearningSetRemove = (event: React.MouseEvent<any, MouseEvent>, modIndex: number, lsIndex: number) => {
    var newList = modules.slice();
    newList[modIndex].learningSets.splice(lsIndex, 1);
    onChange({
      ...event,
      target: {
        ...event.target,
        name: name,
        value: newList
      }
    });
  }

  return (
    <>
      {readonly && modules && modules.length > 0 &&
        <>
          <Row>
            <Col md={12} className={styles.rowTitleSpacing}>
              <Typography variant="smallHeading" component="h3">
                {t("request.modules")}
              </Typography>
            </Col>
          </Row>
          {modules.map((module: Module, index: number) => {
            const requestCorrectionModule = correctionModules && correctionModules.length > index ? correctionModules[index] : undefined;
            return (
              <div key={module.id} className={styles.modulePanel}>
                <Row>
                  <Col md={12}>
                    <StaticField
                      label={t("request.moduleName")}
                      value={module.name}
                      newValue={requestCorrectionModule?.name}
                      className={styles.changedFieldModule}
                    />
                  </Col>
                </Row>
                {module.learningSets.map((learningSet: LearningSet, index: number) => (
                  <Row>
                    <Col md={12}>
                      <StaticField
                        label={t("request.moduleLearningSetName")}
                        value={learningSet.name}
                        newValue={requestCorrectionModule && requestCorrectionModule.learningSets.length > index ?
                          requestCorrectionModule.learningSets[index].name : ""}
                        className={styles.changedFieldModule}
                      />
                    </Col>
                    <Col md={4}>
                      <StaticField
                        label={t("request.moduleLearningSetCode")}
                        value={learningSet.code}
                        newValue={requestCorrectionModule && requestCorrectionModule.learningSets.length > index ?
                          requestCorrectionModule.learningSets[index].code : ""}
                        className={styles.changedFieldModule}
                      />
                    </Col>
                    <Col md={4}>
                      <StaticField
                        label={t("request.moduleLearningSetLevel")}
                        value={learningSet.level}
                        newValue={requestCorrectionModule && requestCorrectionModule.learningSets.length > index ?
                          requestCorrectionModule.learningSets[index].level : ""}
                        className={styles.changedFieldModule}
                      />
                    </Col>
                    <Col md={4}>
                      <StaticField
                        label={t("request.moduleLearningSetCSVET")}
                        value={learningSet.csvet}
                        newValue={requestCorrectionModule && requestCorrectionModule.learningSets.length > index ?
                          requestCorrectionModule.learningSets[index].csvet : ""}
                        className={styles.changedFieldModule}
                      />
                    </Col>
                  </Row>
                ))}
              </div>
            )
          })}
        </>
      }
      {(!readonly
        && (programTypeId === ProgramType.TrainingPrograms
          || programTypeId === ProgramType.CraftsAndArtsTrainingPrograms
          || programTypeId === ProgramType.CraftsSpecializationPrograms
          || programTypeId === ProgramType.RefinementPrograms          
          || programTypeId === ProgramType.NewVocationalCurriculum
        )) &&
        <>
          <ModuleInfo
            name={"modules"}
            getErrorForPath={getErrorForPath}
            modules={modules}
            qualificationType={qualificationTypeId}
            onChange={onChange} />
        </>
      }
    </>
  );
}